import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Send, ArrowRight, Sparkles, AlertTriangle, Eye } from "lucide-react";
import { formatDate, formatINR, ADVICE_CATEGORIES } from "@/lib/constants";
import { useAuth } from "@/lib/auth";

// "3d ago" / "2h ago" — kept minimal, no external date-fns dep needed.
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const fmtL = (n) => `₹${(Number(n || 0) / 1e5).toFixed(1)}L`;

function coverageGapTooltip(a) {
  const m = a.coverage_gap_meta || {};
  const kind = a.coverage_gap_kind;
  if (kind === "term_life_under_10x_income")
    return `Existing ${fmtL(m.existing_cover)} vs target ${fmtL(m.required_cover_10x)} (10× annual income of ${fmtL(m.annual_income)}).`;
  if (kind === "health_under_household_target")
    return `Per-member ${fmtL(m.existing_cover_per_member)} vs target ${fmtL(m.recommended_cover_per_member)} for ${m.household_profile}.`;
  if (kind === "motor_missing_idv") return "IDV was not present on the extracted document — verify with the client.";
  if (kind === "motor_low_idv") return `IDV ${fmtL(m.existing_idv)} is below the ₹1L floor — verify current market value.`;
  return "Auto-drafted from the Insurance Policy Analyzer.";
}

function CoverageGapMeta({ advice }) {
  const m = advice.coverage_gap_meta || {};
  const kind = advice.coverage_gap_kind;
  if (kind === "term_life_under_10x_income") {
    return <span>Existing {fmtL(m.existing_cover)} · Target {fmtL(m.required_cover_10x)}</span>;
  }
  if (kind === "health_under_household_target") {
    return <span>Per member {fmtL(m.existing_cover_per_member)} · Target {fmtL(m.recommended_cover_per_member)}</span>;
  }
  if (kind === "motor_missing_idv" || kind === "motor_low_idv") {
    return <span>IDV · verify with client</span>;
  }
  return null;
}

export default function PlanTab({ client, canEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [advice, setAdvice] = useState([]);
  const [plans, setPlans] = useState([]);
  const [mfCats, setMfCats] = useState([]);
  const [mfFunds, setMfFunds] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [sugBusy, setSugBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiGrounding, setAiGrounding] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [newAdv, setNewAdv] = useState({
    category: ADVICE_CATEGORIES[0], recommendation_text: "",
    recommended_amount: 0, recommended_instrument: "",
    mf_category: "", mf_sub_category: "", mutual_fund_id: "",
  });
  const [invSummary, setInvSummary] = useState("");
  const [protSummary, setProtSummary] = useState("");

  const load = async () => {
    const [{ data: a }, { data: ps }] = await Promise.all([
      api.get(`/clients/${client.id}/advice`),
      api.get(`/clients/${client.id}/plans`),
    ]);
    setAdvice(a);
    setPlans(ps);
    if (isAdmin) {
      try {
        const { data: cats } = await api.get("/mf-categories");
        setMfCats(cats);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  useEffect(() => {
    if (!isAdmin || !newAdv.mf_category) { setMfFunds([]); setSuggested(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/mutual-funds", {
          params: { category: newAdv.mf_category, sub_category: newAdv.mf_sub_category || undefined },
        });
        setMfFunds(data);
      } catch { setMfFunds([]); }
      setSugBusy(true);
      try {
        const { data } = await api.get("/mutual-funds/suggested", {
          params: { category: newAdv.mf_category, sub_category: newAdv.mf_sub_category || undefined, limit: 5 },
        });
        setSuggested(data);
      } catch { setSuggested(null); }
      finally { setSugBusy(false); }
    })();
  }, [isAdmin, newAdv.mf_category, newAdv.mf_sub_category]);

  const mfSubOptions = (mfCats.find((c) => c.category === newAdv.mf_category)?.sub_categories) || [];

  const addAdvice = async () => {
    setBusy(true);
    try {
      await api.post("/advice", { client_id: client.id, ...newAdv, recommended_amount: Number(newAdv.recommended_amount) });
      toast.success("Advice added");
      setDlgOpen(false);
      setNewAdv({ category: ADVICE_CATEGORIES[0], recommendation_text: "", recommended_amount: 0, recommended_instrument: "", mf_category: "", mf_sub_category: "", mutual_fund_id: "" });
      setSuggested(null);
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const aiDraft = async () => {
    const activeIds = advice.filter((a) => a.status !== "Implemented" && a.status !== "Superseded").map((a) => a.id);
    if (activeIds.length === 0) { toast.error("Add at least one advice item first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/plans/draft-ai", { client_id: client.id, advice_ids: activeIds });
      setInvSummary(data.investment_plan_summary || "");
      setProtSummary(data.protection_plan_summary || "");
      setAiGrounding(data.intelligence || null);
      if (data.intelligence?.chunks_used > 0) {
        toast.success(`AI draft ready — grounded in ${data.intelligence.chunks_used} knowledge chunks`);
      } else {
        toast.success("AI draft ready — review and edit before creating the plan");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI drafting failed");
    } finally { setAiBusy(false); }
  };

  const draftPlan = async () => {
    const activeIds = advice.filter((a) => a.status !== "Implemented" && a.status !== "Superseded").map((a) => a.id);
    if (activeIds.length === 0) { toast.error("Add at least one advice item first"); return; }
    if (!invSummary || !protSummary) { toast.error("Write both summaries"); return; }
    setBusy(true);
    try {
      await api.post("/plans", {
        client_id: client.id,
        advice_ids: activeIds,
        investment_plan_summary: invSummary,
        protection_plan_summary: protSummary,
      });
      toast.success("Draft plan created");
      setInvSummary(""); setProtSummary("");
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const share = async (p) => {
    try {
      await api.post(`/plans/${p.id}/share`);
      toast.success("Plan shared with client");
      load();
    } catch { toast.error("Failed"); }
  };

  const shareUrl = (p) => `${window.location.origin}/plan-view/${p.share_token}`;

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-dashboard text-base font-bold">Advice items · 6 categories</h3>
          {canEdit && (
            <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-advice-btn"><Plus className="mr-1 h-3 w-3" /> Add advice</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New advice</DialogTitle></DialogHeader>
                <div className="space-y-3 py-3">
                  <div>
                    <Label>Category</Label>
                    <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={newAdv.category} onChange={(e) => setNewAdv({ ...newAdv, category: e.target.value })} data-testid="advice-category-select">
                      {ADVICE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Recommendation</Label>
                    <Textarea rows={3} value={newAdv.recommendation_text} onChange={(e) => setNewAdv({ ...newAdv, recommendation_text: e.target.value })} data-testid="advice-text-input" />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input type="number" value={newAdv.recommended_amount} onChange={(e) => setNewAdv({ ...newAdv, recommended_amount: e.target.value })} data-testid="advice-amount-input" />
                    </div>
                    <div>
                      <Label>Instrument (internal note)</Label>
                      <Input value={newAdv.recommended_instrument} onChange={(e) => setNewAdv({ ...newAdv, recommended_instrument: e.target.value })} data-testid="advice-instrument-input" placeholder="internal, never shown to client" />
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>Fund category</Label>
                      <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={newAdv.mf_category} onChange={(e) => setNewAdv({ ...newAdv, mf_category: e.target.value, mf_sub_category: "", mutual_fund_id: "" })} data-testid="advice-mfcategory-select">
                        <option value="">—</option>
                        <option>Equity</option><option>Debt</option><option>Hybrid</option><option>Money Market</option>
                      </select>
                    </div>
                    <div>
                      <Label>Sub-category</Label>
                      {isAdmin ? (
                        <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={newAdv.mf_sub_category} onChange={(e) => setNewAdv({ ...newAdv, mf_sub_category: e.target.value, mutual_fund_id: "" })} disabled={!newAdv.mf_category} data-testid="advice-mfsubcategory-select">
                          <option value="">—</option>
                          {mfSubOptions.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      ) : (
                        <Input value={newAdv.mf_sub_category} onChange={(e) => setNewAdv({ ...newAdv, mf_sub_category: e.target.value })} data-testid="advice-mfsubcategory-input" placeholder="e.g. Flexi Cap" />
                      )}
                    </div>
                  </div>
                  {isAdmin ? (
                    <div className="space-y-3">
                      {newAdv.mf_category && (
                        <div className="rounded-md border border-primary/20 bg-primary/[0.03] p-3" data-testid="suggested-funds-panel">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="overline text-primary">Suggested funds · top {suggested?.funds?.length || 0}</span>
                            <span className="text-[10px] text-muted-foreground">
                              Ranked by composite score (Sharpe 50% · 5Y rolling 30% · %periods&gt;20% 20%)
                            </span>
                          </div>
                          {sugBusy && <div className="text-xs text-muted-foreground">Ranking…</div>}
                          {!sugBusy && (!suggested || suggested.funds.length === 0) && (
                            <div className="text-xs text-muted-foreground">
                              No funds in this category yet — add one in Research.
                            </div>
                          )}
                          {!sugBusy && suggested?.funds?.length > 0 && (
                            <ul className="space-y-2">
                              {suggested.funds.map((f, idx) => {
                                const isPicked = newAdv.mutual_fund_id === f.id;
                                return (
                                  <li
                                    key={f.id}
                                    className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-xs transition ${
                                      isPicked ? "border-primary bg-primary/5" : "border-border bg-white"
                                    }`}
                                    data-testid={`suggested-fund-${idx}`}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-foreground">#{idx + 1}</span>
                                        <span className="truncate font-medium">{f.name}</span>
                                      </div>
                                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                                        {f.sub_category}
                                        {f.fund_manager ? ` · ${f.fund_manager}` : ""}
                                      </div>
                                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                        <span>Sharpe: <b className="text-foreground">{f.sharpe_ratio ?? "—"}</b></span>
                                        <span>5Y roll avg: <b className="text-foreground">{f.rolling_5y_avg_pct != null ? `${f.rolling_5y_avg_pct}%` : "—"}</b></span>
                                        <span>%&gt;20%: <b className="text-foreground">{f.pct_periods_returns_above_20 != null ? `${f.pct_periods_returns_above_20}%` : "—"}</b></span>
                                        <span>Score: <b className="text-primary">{f.composite_score.toFixed(2)}</b></span>
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant={isPicked ? "default" : "outline"}
                                      onClick={() => setNewAdv({ ...newAdv, mutual_fund_id: f.id })}
                                      data-testid={`assign-suggested-fund-${idx}`}
                                    >
                                      {isPicked ? "Assigned" : "Assign"}
                                    </Button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                      <div>
                        <Label>Specific mutual fund <span className="text-xs text-primary">(Admin only — not shown to client until approval)</span></Label>
                        <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={newAdv.mutual_fund_id} onChange={(e) => setNewAdv({ ...newAdv, mutual_fund_id: e.target.value })} disabled={!newAdv.mf_category} data-testid="advice-fund-select">
                          <option value="">— pick a fund —</option>
                          {mfFunds.map((f) => <option key={f.id} value={f.id}>{f.name} · {f.sub_category}</option>)}
                        </select>
                        {newAdv.mf_category && mfFunds.length === 0 && <div className="mt-1 text-xs text-muted-foreground">No funds in this category yet — add one in Research.</div>}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid="advice-fund-locked-hint">
                      🔒 Specific fund selection is set by the Principal Advisor.
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={addAdvice} disabled={busy} data-testid="advice-save-btn">Save advice</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <div className="space-y-2">
          {advice.length === 0 && <p className="text-sm text-muted-foreground">No advice drafted yet.</p>}
          {advice.map((a) => (
            <div key={a.id} className={`rounded-md border p-4 ${a.coverage_gap ? "border-primary/30 bg-primary/[0.03]" : "border-border bg-white"}`} data-testid={`advice-item-${a.id}`}>
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="overline text-primary">{a.category}</span>
                  {a.coverage_gap && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary" title={coverageGapTooltip(a)} data-testid={`coverage-gap-badge-${a.id}`}>
                      <AlertTriangle className="h-3 w-3" />
                      Coverage gap
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{a.status}</span>
              </div>
              <div className="text-sm">{a.recommendation_text}</div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                {a.recommended_amount > 0 && <span>Amount · {formatINR(a.recommended_amount)}</span>}
                {a.recommended_instrument && <span>Instrument · {a.recommended_instrument}</span>}
                {a.coverage_gap && <CoverageGapMeta advice={a} />}
              </div>
            </div>
          ))}
        </div>
      </section>

      {canEdit && (
        <Card className="border-border bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-dashboard text-base font-bold">Draft a new plan version</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={aiDraft}
              disabled={aiBusy || advice.length === 0}
              className="gap-1 border-accent/40 text-accent hover:bg-accent/10"
              data-testid="ai-draft-btn"
            >
              <Sparkles className="h-3 w-3" /> {aiBusy ? "Drafting…" : "Draft with AI"}
            </Button>
          </div>
          {aiGrounding && aiGrounding.chunks_used > 0 && (
            <div
              className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-accent/30 bg-accent/5 p-3 text-xs"
              data-testid="ai-grounding-badge"
            >
              <Sparkles className="h-3 w-3 text-accent" />
              <span className="font-semibold text-accent">Grounded in Intelligence</span>
              <span className="text-muted-foreground">
                · {aiGrounding.chunks_used} chunk{aiGrounding.chunks_used !== 1 ? "s" : ""} used
              </span>
              {aiGrounding.source_documents?.length > 0 && (
                <span className="text-muted-foreground">
                  · Sources: {aiGrounding.source_documents.slice(0, 3).join(", ")}
                  {aiGrounding.source_documents.length > 3 && ` +${aiGrounding.source_documents.length - 3} more`}
                </span>
              )}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Investment plan summary</Label>
              <Textarea rows={6} value={invSummary} onChange={(e) => setInvSummary(e.target.value)} placeholder="How we'll build toward the goals." data-testid="investment-summary-input" />
            </div>
            <div>
              <Label>Protection plan summary</Label>
              <Textarea rows={6} value={protSummary} onChange={(e) => setProtSummary(e.target.value)} placeholder="How we'll safeguard them." data-testid="protection-summary-input" />
            </div>
          </div>
          <Button onClick={draftPlan} disabled={busy} className="mt-4" data-testid="create-plan-btn">Create draft plan</Button>
        </Card>
      )}

      <section>
        <h3 className="mb-3 font-dashboard text-base font-bold">Plan versions</h3>
        {plans.length === 0 && <p className="text-sm text-muted-foreground">No plans yet.</p>}
        <div className="space-y-3">
          {plans.map((p) => (
            <Card key={p.id} className="border-border bg-white p-5" data-testid={`plan-${p.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="font-dashboard font-bold">Version {p.version_number}</div>
                  <div className="text-xs text-muted-foreground">Created {formatDate(p.created_at)}</div>
                </div>
                <span className={"stage-chip " + (
                  p.status === "Approved" ? "stage-5" :
                  p.status === "Shared with Client" ? "stage-3" :
                  p.status === "Draft" ? "stage-2" : "stage-1"
                )}>{p.status}</span>
              </div>
              {p.status !== "Draft" && (
                <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid={`plan-viewed-${p.id}`}>
                  {p.shared_at && <span>Shared {timeAgo(p.shared_at)}</span>}
                  {p.first_viewed_at ? (
                    <span className="inline-flex items-center gap-1 text-accent">
                      <Eye className="h-3 w-3" />
                      Opened {timeAgo(p.first_viewed_at)}
                      {p.view_count > 1 && <span className="text-muted-foreground">· viewed {p.view_count} times</span>}
                      {p.last_viewed_at && p.view_count > 1 && p.last_viewed_at !== p.first_viewed_at && (
                        <span className="text-muted-foreground">· last {timeAgo(p.last_viewed_at)}</span>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-primary">
                      <Eye className="h-3 w-3" /> Not yet opened
                    </span>
                  )}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="overline text-muted-foreground">Investment</div>
                  <p className="mt-1 text-sm">{p.investment_plan_summary}</p>
                </div>
                <div>
                  <div className="overline text-muted-foreground">Protection</div>
                  <p className="mt-1 text-sm">{p.protection_plan_summary}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {canEdit && p.status === "Draft" && (
                  <Button size="sm" onClick={() => share(p)} className="gap-1" data-testid={`share-plan-${p.id}`}>
                    <Send className="h-3 w-3" /> Share with client
                  </Button>
                )}
                {p.status !== "Draft" && (
                  <a href={shareUrl(p)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium hover:bg-secondary" data-testid={`view-plan-${p.id}`}>
                    Open client link <ArrowRight className="h-3 w-3" />
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
